// client/src/coms/views/canvas_parts/CanvasPencilSettingsPanel.js
import React from 'react';
import CanvasSurfaceMediaControls from './CanvasSurfaceMediaControls';

function normaliseDegrees(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return ((number % 360) + 360) % 360;
}

function clampElevation(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 90;
  return Math.max(0, Math.min(90, number));
}

function clampPencilGrade(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return Math.max(-8, Math.min(8, Math.round(number)));
}

function formatPencilGrade(value) {
  const grade = clampPencilGrade(value);
  if (grade === 0) return 'HB';
  return grade < 0 ? `${Math.abs(grade)}H` : `${grade}B`;
}

const PENCIL_GRADE_OPTIONS = Array.from({ length: 17 }, (_, index) => {
  const value = index - 8;
  return {
    value,
    label: formatPencilGrade(value),
  };
});

function snapPencilElevation(value) {
  const elevation = clampElevation(value);
  return elevation >= 45 ? 90 : 0;
}

function getPencilOrientation(pencilOrientation) {
  if (pencilOrientation && typeof pencilOrientation === 'object') {
    return {
      azimuth: normaliseDegrees(pencilOrientation.azimuth),
      elevation: snapPencilElevation(pencilOrientation.elevation),
    };
  }

  return {
    azimuth: normaliseDegrees(pencilOrientation),
    elevation: 90,
  };
}

function PencilGradeControl({ value, onChange }) {
  const safeValue = clampPencilGrade(value);

  return (
    <label
      title="Pencil type"
      style={{
        display: 'grid',
        gridTemplateColumns: '88px minmax(0, 1fr)',
        alignItems: 'center',
        gap: 8,
        color: 'rgba(245,247,250,0.92)',
        fontSize: 12,
        fontWeight: 850,
      }}
    >
      <span style={{ color: 'rgba(255,255,255,0.66)' }}>Pencil Type</span>
      <select
        value={safeValue}
        onChange={(event) => {
          if (typeof onChange === 'function') onChange(clampPencilGrade(event.target.value));
        }}
        style={{
          height: 28,
          width: '100%',
          border: '1px solid rgba(255,255,255,0.14)',
          borderRadius: 5,
          background: 'rgba(0,0,0,0.22)',
          color: 'rgba(245,247,250,0.96)',
          padding: '0 8px',
          fontSize: 12,
          fontWeight: 850,
          outline: 'none',
          cursor: 'pointer',
        }}
      >
        {PENCIL_GRADE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </label>
  );
}

function PencilAngleDial({ value, onChange }) {
  const safeOrientation = getPencilOrientation(value);
  const size = 190;
  const cx = size / 2;
  const cy = size / 2;
  const rTrack = size / 2 - 14;
  const rKnob = 7;
  const rNorm = 1 - (safeOrientation.elevation / 90);
  const angleRad = (safeOrientation.azimuth - 90) * (Math.PI / 180);
  const knobX = cx + (rNorm * rTrack * Math.cos(angleRad));
  const knobY = cy + (rNorm * rTrack * Math.sin(angleRad));

  function commitOrientation(nextOrientation) {
    if (typeof onChange !== 'function') return;
    const next = getPencilOrientation(nextOrientation);
    onChange({
      azimuth: normaliseDegrees(next.azimuth),
      elevation: snapPencilElevation(next.elevation),
    });
  }

  function getOrientationFromPointer(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const dx = x - cx;
    const dy = y - cy;
    const angle = Math.atan2(dy, dx);
    const azimuth = normaliseDegrees((angle * 180) / Math.PI + 90);
    const distance = Math.sqrt((dx * dx) + (dy * dy));
    const radius = Math.max(0, Math.min(1, distance / rTrack));
    const snappedRadius = radius >= 0.5 ? 1 : 0;

    return {
      azimuth,
      elevation: snappedRadius === 1 ? 0 : 90,
    };
  }

  function handlePointerDown(event) {
    commitOrientation(getOrientationFromPointer(event));
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  function handlePointerMove(event) {
    if (event.buttons !== 1) return;
    commitOrientation(getOrientationFromPointer(event));
  }

  const tick = (deg, len = 7, width = 2, opacity = 0.5) => {
    const a = (deg - 90) * (Math.PI / 180);
    const x1 = cx + ((rTrack - len) * Math.cos(a));
    const y1 = cy + ((rTrack - len) * Math.sin(a));
    const x2 = cx + ((rTrack + 2) * Math.cos(a));
    const y2 = cy + ((rTrack + 2) * Math.sin(a));

    return (
      <line
        key={`tick-${deg}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="rgba(255,255,255,0.62)"
        strokeWidth={width}
        opacity={opacity}
      />
    );
  };

  const ring = (radius) => (
    <circle
      key={`ring-${radius}`}
      cx={cx}
      cy={cy}
      r={radius}
      fill="none"
      stroke="rgba(255,255,255,0.16)"
      strokeWidth="1.5"
      strokeDasharray="2,4"
      opacity="0.85"
    />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', minWidth: 0 }}>
      <div
        style={{
          alignSelf: 'stretch',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          color: 'rgba(245,247,250,0.92)',
          fontSize: 12,
          fontWeight: 850,
        }}
      >
        <span style={{ color: 'rgba(255,255,255,0.66)' }}>Angle</span>
        <span>{Math.round(safeOrientation.azimuth)}° / {Math.round(safeOrientation.elevation)}°</span>
      </div>
      <svg
        width={size}
        height={size}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        style={{
          display: 'block',
          touchAction: 'none',
          cursor: 'crosshair',
          maxWidth: '100%',
        }}
      >
        <circle cx={cx} cy={cy} r={rTrack} fill="rgba(0,0,0,0.24)" stroke="rgba(255,255,255,0.18)" strokeWidth="2" />
        {tick(0, 10, 2.5, 0.9)}
        {tick(90, 10, 2.5, 0.9)}
        {tick(180, 10, 2.5, 0.9)}
        {tick(270, 10, 2.5, 0.9)}
        {[...Array(12)].map((_, index) => tick(index * 30, 6, 1.5, 0.45))}
        {ring(rTrack * 0.5)}
        <circle cx={cx} cy={cy} r="2.8" fill="rgba(255,255,255,0.56)" />
        <circle cx={knobX} cy={knobY} r={rKnob + 2} fill="rgba(0,0,0,0.90)" stroke="rgba(87,166,255,0.95)" strokeWidth="2.5" />
        <circle cx={knobX} cy={knobY} r={rKnob} fill="rgba(87,166,255,1)" />
      </svg>
      <div
        style={{
          textAlign: 'center',
          fontSize: 12,
          fontWeight: 800,
          color: 'rgba(235,241,252,0.88)',
        }}
      >
        Direction: {Math.round(safeOrientation.azimuth)}° • Angle: {Math.round(safeOrientation.elevation)}°
      </div>
    </div>
  );
}

export default function CanvasPencilSettingsPanel({
  pencilOrientation,
  setPencilOrientation,
  pencilGrade,
  setPencilGrade,
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        minWidth: 0,
      }}
    >
      <PencilGradeControl value={pencilGrade} onChange={setPencilGrade} />
      <PencilAngleDial value={pencilOrientation} onChange={setPencilOrientation} />
      <CanvasSurfaceMediaControls kind="pencil" pencilGrade={pencilGrade} pencilElevation={getPencilOrientation(pencilOrientation).elevation} />
    </div>
  );
}
